import { useCallback, useEffect } from 'react';
import FidesChatAdminService from '../../services/admin/FidesChatAdminService';

function isBrowserActiveForChat(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

/**
 * Tell the backend when this admin is actively viewing a chat (tab visible + window
 * focused). Clears on blur, tab hidden, or unmount — no polling.
 */
export default function useChatViewingPresence(
  conversationId: string | null,
  enabled: boolean,
): void {
  const syncViewing = useCallback((convId: string, shouldTrack: boolean) => {
    if (!shouldTrack || !isBrowserActiveForChat()) {
      void FidesChatAdminService.clearViewing(convId).catch((error) => {
        console.warn('Fides chat: failed to clear viewing presence', error);
      });
      return;
    }

    void FidesChatAdminService.reportViewing(convId).catch((error) => {
      console.warn('Fides chat: failed to report viewing presence', error);
    });
  }, []);

  useEffect(() => {
    if (!enabled || !conversationId) {
      return undefined;
    }

    const onActivityChange = () => {
      syncViewing(conversationId, true);
    };

    document.addEventListener('visibilitychange', onActivityChange);
    window.addEventListener('focus', onActivityChange);
    window.addEventListener('blur', onActivityChange);

    onActivityChange();

    return () => {
      document.removeEventListener('visibilitychange', onActivityChange);
      window.removeEventListener('focus', onActivityChange);
      window.removeEventListener('blur', onActivityChange);
      syncViewing(conversationId, false);
    };
  }, [conversationId, enabled, syncViewing]);
}
